import { CountryFormState } from '../../../../../../store/planStore';

type CurrencyValue = { currency: string; value: number };

type DigitalTechCost = { dsp: number; adServer: number };

export type BudgetDataByCountry = Record<
  string,
  {
    sumForDigital: CurrencyValue;
    digitalTechCost: DigitalTechCost;
    sumForOffline: CurrencyValue;
    offlineTechCost: { value: number; currency: string }[];
  }
>;

export function buildBudgetDataByCountry(
  countryForms: CountryFormState // replace with your real type
): BudgetDataByCountry {
  return Object.entries(countryForms).reduce((countryAcc, [country, forms]) => {
    const digitalBudget = forms.savedCalculatedBudget;
    const offlineBudget = forms.offline?.tv?.offlineBudget;

    const sumForDigital = digitalBudget?.reduce(
      (digitalAcc: CurrencyValue, entry: any) => {
        if (!entry) return digitalAcc;
        return {
          currency: entry.budget.currency,
          value: digitalAcc.value + (entry.budget.value ?? 0),
        };
      },
      { currency: digitalBudget?.[0]?.budget?.currency || 'PLN', value: 0 }
    ) || { currency: 'PLN', value: 0 };

    const digitalTechCost = digitalBudget?.reduce(
      (techAcc: DigitalTechCost, entry: any) => {
        if (!entry?.techCost) return techAcc;
        return {
          dsp: techAcc.dsp + (entry.techCost.dsp ?? 0),
          adServer: techAcc.adServer + (entry.techCost.adServer ?? 0),
        };
      },
      { dsp: 0, adServer: 0 }
    ) || { dsp: 0, adServer: 0 };

    const sumForOffline = offlineBudget
      ?.filter((entry: any) => entry.touchpoint !== 'tech cost')
      ?.reduce(
        (offlineAcc: CurrencyValue, entry: any) => {
          if (!entry?.budget) return offlineAcc;
          return {
            currency: entry.budget.currency,
            value: offlineAcc.value + (entry.budget.value ?? 0),
          };
        },
        { currency: offlineBudget?.[0]?.budget?.currency || 'PLN', value: 0 }
      ) || { currency: 'PLN', value: 0 };

    const offlineTechCost = offlineBudget
      ?.filter((entry: any) => entry.touchpoint === 'tech cost')
      .map((entry: any) => ({
        value: entry.budget.value,
        currency: entry.budget.currency,
      })) || [{ value: 0, currency: 'PLN' }];

    return {
      ...countryAcc,
      [country]: {
        sumForDigital,
        digitalTechCost,
        sumForOffline,
        offlineTechCost,
      },
    };
  }, {} as BudgetDataByCountry);
}

export function getTotalBudget(
  budgetDataByCountry: BudgetDataByCountry
): number {
  return Object.values(budgetDataByCountry).reduce(
    (acc, countryData) =>
      acc +
      (countryData.sumForDigital.value ?? 0) +
      (countryData.sumForOffline.value ?? 0),
    0
  );
}

export function getFirstCurrency(
  budgetDataByCountry: BudgetDataByCountry
): string {
  return (
    Object.values(budgetDataByCountry)
      .map(
        (countryData) =>
          countryData.sumForDigital.currency ||
          countryData.sumForOffline.currency
      )
      .find((currency) => currency) || 'PLN'
  );
}

export function getTotalTechCost(
  budgetDataByCountry: BudgetDataByCountry
): number {
  return Object.values(budgetDataByCountry).reduce(
    (acc, countryData) =>
      acc +
      (countryData.digitalTechCost.dsp ?? 0) +
      (countryData.digitalTechCost.adServer ?? 0) +
      countryData.offlineTechCost.reduce(
        (offlineAcc, entry) => offlineAcc + (entry.value ?? 0),
        0
      ),
    0
  );
}

export function getBudgetTotals(countryForms: any) {
  const budgetDataByCountry = buildBudgetDataByCountry(countryForms);
  return {
    budgetDataByCountry,
    totalBudget: getTotalBudget(budgetDataByCountry),
    firstCurrency: getFirstCurrency(budgetDataByCountry),
    totalTechCost: getTotalTechCost(budgetDataByCountry),
  };
}

type StoredDdx =
  | { type: 'omitted'; value: number; totalDDX?: number }
  | { type: 'percentage'; value: number; totalDDX?: number }
  | { type: 'fixed'; value: number; totalDDX?: number }
  | { type: null; value: null; totalDDX: null }
  | null
  | undefined;

export function isDdxNullsSentinel(ddx: any): boolean {
  return (
    ddx != null && ddx.type == null && ddx.value == null && ddx.totalDDX == null
  );
}

export function isDdxNotApplicableForAllCountries(
  countryForms: Record<string, any>
): boolean {
  return Object.values(countryForms).every((countryForm) =>
    isDdxNullsSentinel(countryForm.ddx)
  );
}

function isDdxPicked(ddx: any): boolean {
  if (!ddx) return false;

  if (ddx.type === 'omitted') return true;

  if (ddx.type === 'percentage' || ddx.type === 'fixed') {
    return typeof ddx.value === 'number' && Number.isFinite(ddx.value);
  }

  return false;
}

export const isDdxPickedForAllCountries = (
  countryForms: Record<string, Partial<CountryFormState>>
): boolean => {
  return Object.values(countryForms).every((countryForm) => {
    const ddx = countryForm.ddx as any;

    const isNullsSentinel =
      ddx != null &&
      ddx.type == null &&
      ddx.value == null &&
      ddx.totalDDX == null;
    if (isNullsSentinel) return true;

    if (!ddx) return false;
    if (ddx.type === 'omitted') return true;
    if (ddx.type === 'percentage' || ddx.type === 'fixed') {
      return typeof ddx.value === 'number' && Number.isFinite(ddx.value);
    }

    return false;
  });
};
